import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { MouseEvent, MutableRefObject, useCallback, useRef } from "react";
import {
  Edge,
  Node,
  NodeChange,
  NodeMouseHandler,
  NodePositionChange,
  EdgeChange,
  ReactFlowInstance,
  useReactFlow,
  Connection,
  type NodeSelectionChange,
} from "@xyflow/react";
import {
  nodesAtom,
  edgesAtom,
  isViewOnlyModeAtom,
  cursorAtom,
  addEdgeSetter,
  isDraggingNodeAtom,
  nodeContextMenuAtom,
  selectedNodeTypeAtom,
  takeSnapshotSetter,
  adjustZapStepsPositionSetter,
  horizontalHelperLineAtom,
  verticalHelperLineAtom,
  getNodePositionInsideParent,
  sidebarOpenNodeIdAtom,
  saveCanvasSetter,
  deselectAllNodesSetter,
  isReconnectingEdgeInProgressAtom,
  updateEdgeConnectionSetter,
  isConnectingEdgeInProgressAtom,
  hasStreamingProgressStartedAtom,
  getAbsoluteNodePosition,
  isEditingTextNodeAtomFamily,
} from "@zapier/canvas-state";
import { TEMP } from "@zapier/canvas-constants";

import { applyEdgeChanges, applyNodeChanges } from "../../utils/changes";
import updateNodePositions from "../../utils/updateNodePositions";
import { sortNodes } from "../../utils/sortNodes";
import { updateClassNames } from "../../utils/updateClassNames";

import { getHelperLines } from "./utils";

export function useReactFlowCallbacks(
  ref?: MutableRefObject<HTMLDivElement | null>,
) {
  const setHelperLineHorizontal = useSetAtom(horizontalHelperLineAtom);
  const setHelperLineVertical = useSetAtom(verticalHelperLineAtom);
  const [isDraggingNode, setIsDraggingNode] = useAtom(isDraggingNodeAtom);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const [nodes, setNodes] = useAtom(nodesAtom);
  const setEdges = useSetAtom(edgesAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const { screenToFlowPosition, getIntersectingNodes } = useReactFlow();
  const cursorMode = useAtomValue(cursorAtom);
  const selectedNodeType = useAtomValue(selectedNodeTypeAtom);
  const addEdge = useSetAtom(addEdgeSetter);
  const deselectAllNodes = useSetAtom(deselectAllNodesSetter);
  const setNodeContextMenu = useSetAtom(nodeContextMenuAtom);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const adjustZapStepsPosition = useSetAtom(adjustZapStepsPositionSetter);
  const [sidebarOpenNodeId, setSidebarOpenNodeId] = useAtom(
    sidebarOpenNodeIdAtom,
  );
  const updateEdges = useSetAtom(updateEdgeConnectionSetter);

  const hasStreamingProgressStarted = useAtomValue(
    hasStreamingProgressStartedAtom,
  );

  const onInitSetViewport = useCallback(
    (reactFlowInstance: ReactFlowInstance) => {
      /* If streaming nodes, let the custom panning logic (handled when nodes stream in -
       * see useEffect in Canvas.tsx) control the viewport. Otherwise, fall back to the
       * default fitView behavior. */

      if (hasStreamingProgressStarted) {
        return;
      }

      reactFlowInstance.fitView({
        // With new node designs, we were too zoomed in at 1.2
        maxZoom: 0.9,
      });
    },
    [hasStreamingProgressStarted],
  );

  const customApplyNodeChanges = useCallback(
    (changes: NodeChange[], nodes: Node[]): Node[] => {
      // reset the helper lines (clear existing lines, if any)
      setHelperLineHorizontal(undefined);
      setHelperLineVertical(undefined);

      // @ts-ignore it does have an id though
      const changedNode = nodes.find((n) => n.id === changes[0].id);

      // this will be true if it's a single node being dragged
      // inside we calculate the helper lines and snap position for the position where the node is being moved to
      if (
        changedNode &&
        changes[0].type === "position" &&
        changes[0].dragging &&
        changes[0].position &&
        changedNode?.parentId === undefined
      ) {
        const helperLines = getHelperLines(changes[0], nodes);

        // if we have a helper line, we snap the node to the helper line position
        // this is being done by manipulating the node position inside the change object
        changes[0].position.x =
          helperLines.snapPosition.x ?? changes[0].position.x;
        changes[0].position.y =
          helperLines.snapPosition.y ?? changes[0].position.y;

        // if helper lines are returned, we set them so that they can be displayed
        setHelperLineHorizontal(helperLines.horizontal);
        setHelperLineVertical(helperLines.vertical);
      }

      return applyNodeChanges(changes, nodes);
    },
    [setHelperLineHorizontal, setHelperLineVertical],
  );

  const onNodeContextMenu: NodeMouseHandler = useAtomCallback(
    useCallback(
      (get, _set, event, node) => {
        // Allow native context menu for text nodes in edit mode (e.g., for spell check)
        if (node.type === "text") {
          const isEditingText = get(isEditingTextNodeAtomFamily(node.id));
          if (isEditingText) {
            return;
          }
        }

        // Prevent native context menu from showing
        event.preventDefault();

        /** deletable is true by default, so we need to check for an explicit false */
        if (!ref?.current || isViewOnlyMode || node.deletable === false) {
          return;
        }

        // Calculate position of the context menu. We want to make sure it
        // doesn't get positioned off-screen.
        const pane = ref?.current.getBoundingClientRect();
        setNodeContextMenu({
          id: node.id,
          top:
            event.clientY < (pane?.height ?? 0) - 200
              ? event.clientY - 50
              : undefined,
          left:
            event.clientX < (pane?.width ?? 0) - 200
              ? event.clientX
              : undefined,
        });
      },
      [isViewOnlyMode, setNodeContextMenu, ref],
    ),
  );

  const nodeChangesRef = useRef(new Map());

  const onNodesChange = useCallback(
    async (nodeChanges: NodeChange[]) => {
      const shouldCloseSidebarOnDeselect = nodeChanges.some((nodeChange) => {
        return (
          isNodeSelectionChange(nodeChange) &&
          nodeChange.id === sidebarOpenNodeId &&
          !nodeChange.selected
        );
      });

      /* We need this so that when a node is deselected, we close the sidebar.
      Or else when you select the node again, the sidebar will already be open */
      if (shouldCloseSidebarOnDeselect) {
        setSidebarOpenNodeId("");
      }

      adjustZapStepsPosition(nodeChanges);

      const hasPositionsChanged = nodeChanges.every(
        (nodeChange) => nodeChange.type === "position" && nodeChange.dragging,
      );

      const hasPositionsChangedNotDragging = nodeChanges.every(
        (nodeChange) => nodeChange.type === "position" && !nodeChange.dragging,
      );

      if (hasPositionsChanged) {
        (nodeChanges as NodePositionChange[]).forEach((nodeChange) => {
          // Update the ref with the latest node positions
          nodeChangesRef.current.set(nodeChange.id, nodeChange);
        });
      }

      /** We are saving here instead of onNodeDragStop since onNodeDragStop only runs after you drag 1 node, and we need to save the position when multiple nodes are dragged. */
      if (hasPositionsChangedNotDragging) {
        const updatedNodes = updateNodePositions(nodes, nodeChangesRef);
        saveToBackend({ nodes: updatedNodes as Node[] });
      }

      // We don't want to take history snapshots while the user is in the middle
      // of dragging a node. It would add an incredible amount of noise to the history.
      if (!isDraggingNode) {
        takeSnapshot();
      }

      setNodes((nodes) => customApplyNodeChanges(nodeChanges, nodes));
    },
    [
      nodes,
      setNodes,
      saveToBackend,
      takeSnapshot,
      isDraggingNode,
      adjustZapStepsPosition,
      customApplyNodeChanges,
      sidebarOpenNodeId,
      setSidebarOpenNodeId,
    ],
  );

  const onEdgesChange = useCallback(
    (edgeChanges: EdgeChange[]) => {
      if (isViewOnlyMode) {
        // We don't want to open the PathNode toolbar
        return;
      }

      setEdges((edges) => applyEdgeChanges(edgeChanges, edges));
    },
    [isViewOnlyMode, setEdges],
  );

  const onPaneMouseMove = useCallback(
    (event: MouseEvent) => {
      const reactFlowBounds = ref?.current?.getBoundingClientRect();
      const hasSelectedNodeFromDnDToolbar =
        selectedNodeType !== null && selectedNodeType !== "group";

      if (hasSelectedNodeFromDnDToolbar && reactFlowBounds) {
        const rawPosition = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        /** Need the offset so the user is able to click the pane to create a new node */
        const offSet = 14;

        const position = {
          x: rawPosition.x + offSet,
          y: rawPosition.y + offSet,
        };

        const tempNode = {
          id: TEMP,
          type: TEMP,
          position,
          data: { type: selectedNodeType },
          width: 60,
          height: 60,
        };

        setNodes((ns) => {
          const tempNodeExists = ns.some((node) => node.id === TEMP);

          if (tempNodeExists) {
            return ns.map((node) => {
              if (node.id === TEMP) {
                return {
                  ...tempNode,
                  position,
                };
              }
              return node;
            });
          }

          return [...ns, tempNode];
        });
      }
    },
    [ref, selectedNodeType, screenToFlowPosition, setNodes],
  );

  const onNodeDragStart = useCallback(() => {
    setIsDraggingNode(true);
  }, [setIsDraggingNode]);

  const onNodeDrag = useAtomCallback(
    useCallback(
      (_, set, _event: React.MouseEvent, node: Node) => {
        const intersections = getIntersectingNodes(node).map((n) => n.id);

        set(nodesAtom, (ns) => {
          // Early return optimization - skip parent logic if no parent
          if (!node.parentId) {
            return ns.map((n) => ({
              ...n,
              className: updateClassNames(
                n.className || "",
                "highlight",
                intersections.includes(n.id),
              ),
            }));
          }

          return ns.map((n) => {
            let shouldHighlight = false;

            // Highlight when entering a NEW group (not its current parent)
            if (intersections.includes(n.id) && node.parentId !== n.id) {
              shouldHighlight = true;
            }

            // Highlight parent group when child is crossing the boundary or has exited
            if (n.id === node.parentId) {
              const parentNode = n;
              const childAbsolutePos = getAbsoluteNodePosition(node, ns);

              if (
                childAbsolutePos &&
                parentNode.position &&
                node.measured &&
                parentNode.measured
              ) {
                const childRight =
                  childAbsolutePos.x + (node.measured.width || 0);
                const childBottom =
                  childAbsolutePos.y + (node.measured.height || 0);
                const parentRight =
                  parentNode.position.x + (parentNode.measured.width || 0);
                const parentBottom =
                  parentNode.position.y + (parentNode.measured.height || 0);

                // Check if child is fully contained within parent bounds
                const isFullyInside =
                  childAbsolutePos.x >= parentNode.position.x &&
                  childAbsolutePos.y >= parentNode.position.y &&
                  childRight <= parentRight &&
                  childBottom <= parentBottom;

                // Highlight whenever child is not fully inside (crossing boundary or outside)
                shouldHighlight = !isFullyInside;
              }
            }

            return {
              ...n,
              className: updateClassNames(
                n.className || "",
                "highlight",
                shouldHighlight,
              ),
            };
          });
        });
      },
      [getIntersectingNodes],
    ),
  );

  const onNodeDragStop = useCallback(
    (_: any, node: Node) => {
      setIsDraggingNode(false);

      if (node.type === "path") {
        deselectAllNodes(); // Or else the sidebar will open after you finish dragging the path node (other node types have toolbars)
      }

      let nextNodes = nodes;

      // Skip group membership logic for group nodes themselves (node.type !== "group")
      // to prevent nesting groups inside groups, which causes infinite loops when
      // moving the parent group. TODO: Fix the infinite loop to allow groups within groups.
      if (cursorMode !== "connect" && node.type !== "group") {
        // Find all group nodes that the dragged node is currently overlapping with
        const intersectingGroups = getIntersectingNodes(node).filter(
          (n) => n.type === "group",
        );
        // Use the first intersecting group as the target (if any)
        const targetGroup = intersectingGroups[0];

        // Check if node was inside a group but is now dragged outside of it
        const isExitingGroup =
          node.parentId &&
          !intersectingGroups.some((n) => n.id === node.parentId);

        // Check if node is being dragged into a new group (not its current parent)
        const isEnteringGroup = targetGroup && node.parentId !== targetGroup.id;

        if (isExitingGroup) {
          // Node is leaving its parent group:
          // - Clear the parentId to remove the relationship
          // - Convert position from relative (to parent) to absolute (canvas coordinates)
          const absolutePosition = getAbsoluteNodePosition(node, nodes);
          nextNodes = nodes.map((n) =>
            n.id === node.id
              ? { ...n, parentId: undefined, position: absolutePosition }
              : n,
          );
        } else if (isEnteringGroup) {
          // Node is entering a group:
          // - Set parentId to establish the relationship
          // - Convert position from absolute to relative (to the group's position)
          const position = getNodePositionInsideParent(node, targetGroup) ?? {
            x: 0,
            y: 0,
          };
          nextNodes = nodes.map((n) =>
            n.id === node.id ? { ...n, position, parentId: targetGroup.id } : n,
          );
        }
      }

      // Clear all highlight classes (shown during drag) and ensure proper render order
      const finalNodes = nextNodes
        .map((n) => ({ ...n, className: "" }))
        .sort(sortNodes) as Node[];

      setNodes(finalNodes);
    },
    [
      setIsDraggingNode,
      cursorMode,
      deselectAllNodes,
      getIntersectingNodes,
      nodes,
      setNodes,
    ],
  );

  const onConnect = useCallback(
    async (connection: Connection) => {
      addEdge(connection);
    },
    [addEdge],
  );

  const setIsConnectingEdgeInProgress = useSetAtom(
    isConnectingEdgeInProgressAtom,
  );

  const onConnectStart = useCallback(() => {
    if (isViewOnlyMode) return;

    deselectAllNodes();
    setIsConnectingEdgeInProgress(true);
  }, [deselectAllNodes, isViewOnlyMode, setIsConnectingEdgeInProgress]);

  const onConnectEnd = useCallback(() => {
    setIsConnectingEdgeInProgress(false);
  }, [setIsConnectingEdgeInProgress]);

  const setIsReconnectionInProgress = useSetAtom(
    isReconnectingEdgeInProgressAtom,
  );

  const onReconnectStart = useCallback(() => {
    if (isViewOnlyMode) return;

    deselectAllNodes();
    setIsReconnectionInProgress(true);
  }, [deselectAllNodes, isViewOnlyMode, setIsReconnectionInProgress]);

  const onReconnectEnd = useCallback(() => {
    setIsReconnectionInProgress(false);
  }, [setIsReconnectionInProgress]);

  const onReconnect = useCallback(
    (oldEdge: Edge, newConnection: Connection) =>
      updateEdges({ oldEdge, newConnection }),
    [updateEdges],
  );

  return {
    onInitSetViewport,
    onNodesChange,
    onEdgesChange,
    onNodeContextMenu,
    onPaneMouseMove,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    onConnect,
    onConnectStart,
    onConnectEnd,
    onReconnect,
    onReconnectStart,
    onReconnectEnd,
  };
}

function isNodeSelectionChange(
  change: NodeChange,
): change is NodeSelectionChange {
  return change.type === "select";
}
